"use client";
// components/SearchBox.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import useSuggestionsV2 from "@/hooks/use-suggestions-v2";
import ProjectType from "./ProjectTypeIcon";
import { useRouter } from "next/navigation";
import { MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import Link from "next/link";
import { AuthContext } from "@/context/auth-context";
import posthog from "posthog-js";

type SearchBoxV2Params = {
  showGeoDisclosures?: boolean;
  placeHolderText: string[];
  useLinksForResults: boolean;
  onSearch?: (text: string) => void;
  onTextChange?: (params: {
    text?: string;
    projectName?: string;
    projectId?: string;
    villageName?: string;
    villageId?: number;
  }) => void;
  resultTypes?: "projects" | "localities" | "all";
};

function ResultRow({
  suggestion,
  index,
  onClick,
  useLinksForResults,
}: {
  suggestion: any;
  index: number;
  onClick?: (suggestion: any) => void;
  useLinksForResults: boolean;
}) {
  const isAdmin = window.location.pathname.includes("admin");

  const getBasePath = () => {
    return isAdmin ? "/admin/project/" : "/project/";
  };

  return (
    <>
      {suggestion.resultType === "project" && (
        <Link
          href={useLinksForResults ? `${getBasePath()}${suggestion.uuid}` : "#"}
        >
          <div
            key={index}
            className="flex grow p-4 cursor-pointer hover:bg-gray-100 items-center border-b border-gray-200"
            onClick={() => onClick && onClick(suggestion)}
          >
            <ProjectType projectType={suggestion.projectType} size={20} />
            <p className="ml-2 mr-1 grow basis-8/12 truncate uppercase font-semibold text-gray-700 text-xs md:text-sm">
              {suggestion.alias || suggestion.projectName}{" "}
            </p>
            <p className="text-[10px] md:text-xs text-gray-500 capitalize basis-4/12 flex-shrink truncate text-right">
              By {suggestion.developerName.toLowerCase()}
            </p>
          </div>
        </Link>
      )}
      {suggestion.resultType === "village" && (
        <Link href={onClick ? "#" : `/locality/${suggestion.id}/v2`}>
          <div
            key={index}
            className="flex grow p-4 cursor-pointer hover:bg-gray-100 items-center border-b border-gray-200"
            onClick={() => onClick && onClick(suggestion)}
          >
            <MapPin className="mr-2" size={20} />
            <p className="mr-1 text-xs md:text-sm grow font-semibold">
              {suggestion.name}
            </p>
            <p className="text-[10px] md:text-xs text-gray-500">
              ,{" "}
              {suggestion.districtName}
            </p>
          </div>
        </Link>
      )}
    </>
  );
}

const SearchBoxV2: React.FC<SearchBoxV2Params> = ({
  showGeoDisclosures = false,
  placeHolderText,
  onTextChange = () => {},
  useLinksForResults = false,
  resultTypes = "all",
}: SearchBoxV2Params) => {
  // internal variables
  const [query, setQuery] = useState("");
  const [suggestionClicked, setSuggestionClicked] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState<string>();
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout>();
  const [currentPlaceholder, setCurrentPlaceholder] = useState<string>();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const onSearch = (query: string) => {
    posthog.capture("search_performed", {
      query,
    });
    router.push(`/search?query=${encodeURIComponent(query)}`);
  }

  useEffect(() => {
    if (Array.isArray(placeHolderText) && placeHolderText.length > 0) {
      let index = Math.floor(Math.random() * placeHolderText.length);
      const interval = setInterval(() => {
        setCurrentPlaceholder(placeHolderText[index]);
        index = (index + 1) % placeHolderText.length;
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [placeHolderText]);

  const { data: suggestions, loading: suggestionsLoading } =
    useSuggestionsV2(debouncedQuery);

  useEffect(() => {
    if (suggestionClicked) {
      return;
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    setDebounceTimer(
      setTimeout(() => {
        setDebouncedQuery(query);
        onTextChange({ text: query });
      }, 300)
    );
  }, [query]);

  const handleInputChange = (e: any) => {
    setSuggestionClicked(false);
    setQuery(e?.target?.value);
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (suggestion.resultType === "project") {
      setSuggestionClicked(true);
      onTextChange({
        projectName: suggestion.projectName,
        projectId: suggestion.uuid,
      });
      setQuery(suggestion.projectName);
      setDebouncedQuery(undefined);
      posthog.capture("search_suggestion_clicked", {
        query: suggestion.projectName,
        resultType: "project",
        projectId: suggestion.uuid,
        developerName: suggestion.developerName
      });
    }
    if (suggestion.resultType === "village") {
      setSuggestionClicked(true);
      onTextChange({ villageName: suggestion.name, villageId: suggestion.id });
      setQuery(suggestion.name);
      setDebouncedQuery(undefined);
      posthog.capture("search_suggestion_clicked", {
        query: suggestion.name,
        resultType: "village",
        villageId: suggestion.id,
        districtName: suggestion.districtName
      });
    }
  };

  return (
    <div className="relative flex items-center w-full rounded-lg">
      <div className="flex flex-col w-full gap-2">
        <div className="relative flex grow">
          <div className="flex items-center w-full">
            <div className="relative w-full">
              <input
                ref={inputRef}
                type="text"
                placeholder={currentPlaceholder}
                className="grow p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-left text-base h-14 w-full pr-10 bg-base-100"
                value={query}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onSearch(query);
                  }
                }}
              />
              {suggestionsLoading && (
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <span className="loading loading-spinner loading-xs"></span>
                </div>
              )}
            </div>
          </div>
        </div>
        {showGeoDisclosures && (
          <div className="flex justify-center text-xs opacity-50">
            <div>Currently only in Telangana.</div>
          </div>
        )}
      </div>
      {suggestions.length > 0 && query && (
        <div className="absolute z-10 bg-white border border-gray-300 rounded-lg top-full mt-1 w-full shadow-lg">
          {suggestions
            .filter((s) => {
              return (
                resultTypes === "all" ||
                (resultTypes === "projects" && s.resultType === "project") ||
                (resultTypes === "localities" && s.resultType === "village")
              );
            })
            .map((suggestion, index) => (
              <ResultRow
                useLinksForResults={useLinksForResults}
                suggestion={suggestion}
                index={index}
                onClick={handleSuggestionClick}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default SearchBoxV2;
